import { GroupResourceType } from '../types/zod/groupResource';
import { BareGroupBooking, BareVenueRequest } from '../types/groupBooking';
import { DEFAULT_TIMESTR_FORMAT, getLocaleUTCDateString, timeCompare, timeToString } from './time';
import { groupBy, mapBy, MappedItems } from './functional';
import { Brand, HourFormat } from '../../shared/types/brand';
import { BareVenueTemplate } from '../../shared/types/venueTemplate';
import { getCommonTexts } from './brand';
import { capitalize, capitalizeClean } from './string';

export const DEFAULT_TOUR_LEADER = 'Tour Leader';
export const DEFAULT_TOUR_LEADER_CABIN = 'TL Cabin';
export const DEFAULT_GUEST_EXPERIENCE = 'Guest Experience';
export const DEFAULT_GUESTS = 'Guests';
export const DEFAULT_CONTACT_NAME = 'Agent Name';
export const DEFAULT_EMBARK_DATE = 'Embark';
export const DEFAULT_DEBARK_DATE = 'Debark';
export const DEFAULT_GROUP_BOOKING = 'Group Event Details';
export const DEFAULT_HOSPITALITY_DESK = 'Hospitality Desk';
export const DEFAULT_AGENCY_CONTACT_NAME = 'Agency Contact Name';
export const DEFAULT_SALES_DEPT_EMAIL = 'Sales Dept Email';
export const DEFAULT_SALES_DEPT_NUMBER = 'Sales Dept Number';
export const DEFAULT_PARTY_NAME = 'Group Name';
export const DEFAULT_GROUP_ID = 'Group Id';
export const DEFAULT_GROUP_TYPE = 'Group Type';
export const DEFAULT_CABIN_DROPS = 'Cabin Drops';
export const DEFAULT_DINING_PREFERENCE = 'Dining Preference';
export const DEFAULT_PREPAID_PACKAGES = 'Prepaid Packages';
export const DEFAULT_SALES_OFFICE = 'Sales Office';
export const DEFAULT_BOOKING_NO = 'Booking No.';
export const DEFAULT_GROUP_NOTES = 'Group Notes';
export const DEFAULT_GROUP_FILES = 'Group Files';

interface SimpleFields {
  name: string;
  getOld: () => string | number | undefined;
  getNew: () => string | number | undefined;
  sufix?: string;
  prefix?: string;
}

export function findVenueRequestDifferences(
  oldR: BareVenueRequest,
  newR: BareVenueRequest,
  hourFormat: HourFormat,
  allVenues: BareVenueTemplate[],
  brand: Brand
): string[] {
  const changes: string[] = [];
  const { group_booking, guests } = getCommonTexts(
    {
      default: brand.commonTexts,
      translations: [],
    },
    { selected: 'en' }
  );

  const venuesDict = mapBy(allVenues, '_id');

  const simpleFields: SimpleFields[] = [
    { name: 'Venue', getOld: () => oldR.venue, getNew: () => newR.venue },
    { name: group_booking, getOld: () => oldR.name, getNew: () => newR.name },
    { name: guests, getOld: () => oldR.numGuests, getNew: () => newR.numGuests },
    { name: 'Duration', getOld: () => oldR.duration, getNew: () => newR.duration, sufix: 'min' },
    { name: 'Notes', getOld: () => oldR.notes || 'None', getNew: () => newR.notes || 'None' },
    {
      name: 'Internal Notes',
      getOld: () => oldR.internalNotes || 'None',
      getNew: () => newR.internalNotes || 'None',
    },
    { name: 'Venue Configuration', getOld: () => oldR.venueConfig, getNew: () => newR.venueConfig },
    { name: 'Subgroup', getOld: () => oldR.subgroup, getNew: () => newR.subgroup },
  ];

  simpleFields.forEach(field => {
    const oldVal = field.getOld();
    const newVal = field.getNew();
    if (oldVal !== newVal) {
      if (field.name === 'Venue') {
        const oldVenueName = getVenueName(oldVal, venuesDict);
        const newVenueName = getVenueName(newVal, venuesDict);
        changes.push(`${field.name} Update: ${oldVenueName} → ${newVenueName}`);
      } else if (field.name === 'Venue Configuration') {
        changes.push(
          // eslint-disable-next-line max-len
          `${field.name} Update: ${getConfigName(allVenues, oldVal, oldR.venue)} → ${getConfigName(
            allVenues,
            newVal,
            newR.venue
          )}`
        );
      } else {
        changes.push(
          // eslint-disable-next-line max-len
          `${field.name} Update: ${field.prefix ?? ''}${formatValue(oldVal)}${
            field.sufix ?? ''
          } → ${field.prefix ?? ''}${formatValue(newVal)}${field.sufix ?? ''}`
        );
      }
    }
  });

  // StartTime
  if (timeCompare(oldR.startTime, newR.startTime) !== 0) {
    changes.push(
      // eslint-disable-next-line max-len
      `Time Update: ${timeToString(
        oldR.startTime,
        hourFormat,
        DEFAULT_TIMESTR_FORMAT
      )} → ${timeToString(newR.startTime, hourFormat, DEFAULT_TIMESTR_FORMAT)}`
    );
  }

  if (
    oldR.bufferTime?.setUpTime !== newR.bufferTime?.setUpTime ||
    oldR.bufferTime?.breakdownTime !== newR.bufferTime?.breakdownTime
  ) {
    const oldSetUp = oldR.bufferTime?.setUpTime ?? 0;
    const newSetUp = newR.bufferTime?.setUpTime ?? 0;
    const oldBreakdown = oldR.bufferTime?.breakdownTime ?? 0;
    const newBreakdown = newR.bufferTime?.breakdownTime ?? 0;

    changes.push(
      // eslint-disable-next-line max-len
      `Buffer Time Update: Setup ${oldSetUp}min → ${newSetUp}min, Breakdown ${oldBreakdown}min → ${newBreakdown}min`
    );
  }

  // Date
  if (oldR.date.getTime() !== newR.date.getTime()) {
    changes.push(
      `Date Update: ${getLocaleUTCDateString(oldR.date)} → ${getLocaleUTCDateString(newR.date)}`
    );
  }

  const allResourceTypes = new Set([
    ...oldR.resources.map(r => r.resourceType),
    ...newR.resources.map(r => r.resourceType),
  ]);

  let hasResourceChanges = false;
  const resourceChanges: string[] = [];

  allResourceTypes.forEach(resourceType => {
    const typeName = getResourceTypeName(resourceType);
    const oldItems = oldR.resources.filter(r => r.resourceType === resourceType);
    const newItems = newR.resources.filter(r => r.resourceType === resourceType);

    const oldByName = mapBy(oldItems, 'name');
    const newByName = mapBy(newItems, 'name');
    const allNames = new Set([...Object.keys(oldByName), ...Object.keys(newByName)]);

    const itemChanges: string[] = [];
    allNames.forEach(name => {
      const oldItem = oldByName[name];
      const newItem = newByName[name];
      if (oldItem && !newItem) {
        itemChanges.push(`${name} (${oldItem.amount}) → [deleted]`);
      } else if (!oldItem && newItem) {
        itemChanges.push(`[added] → ${name} (${newItem.amount})`);
      } else if (oldItem && newItem && oldItem.amount !== newItem.amount) {
        itemChanges.push(`${name} (${oldItem.amount}) → ${name} (${newItem.amount})`);
      }
    });

    if (itemChanges.length > 0) {
      hasResourceChanges = true;
      resourceChanges.push(`${typeName}: ${itemChanges.join(', ')}`);
    }
  });

  // Resources
  if (hasResourceChanges) {
    changes.push(`Resource Update:`);
    resourceChanges.forEach(change => changes.push(change));
  }

  // Files
  const fileMessage = getFileChangeMessage(oldR, newR);
  if (fileMessage) changes.push(fileMessage);

  return changes;
}

interface WithFiles {
  files: { _id: string; name: string }[];
}

function getFileIds(req: WithFiles): string {
  return req.files
    .map(f => f._id)
    .sort()
    .join('|');
}

function getFileNames(req: WithFiles): string {
  return req.files
    .map(f => f.name)
    .sort()
    .join(', ');
}

function getFileChangeMessage(oldR: WithFiles, newR: WithFiles): string | undefined {
  const oldIds = getFileIds(oldR);
  const newIds = getFileIds(newR);

  if (oldIds !== newIds) {
    return `Files Update: [${getFileNames(oldR) || 'None'}] → [${getFileNames(newR) || 'None'}]`;
  }

  return undefined;
}

function formatValue(value: string | number | undefined): string {
  if (!value && value !== 0) return 'None';
  return String(value);
}

function getResourceTypeName(type: string): string {
  const knownTypes: Record<any, string> = {
    [GroupResourceType.FB]: 'Food & Beverage',
    [GroupResourceType.ENT]: 'Entertainment',
    [GroupResourceType.HSK]: 'Housekeeping',
  };
  return knownTypes[type] || type;
}

function getConfigName(
  venues: BareVenueTemplate[],
  value: string | undefined | number,
  venueId: string
) {
  if (typeof value === 'number') return '';
  if (!value) return 'None';

  const configs = venues.find(v => venueId === v._id)?.configurations;
  const config = configs?.find(c => c._id === value);
  return config ? `${config.name} (occ. ${config.occupancy})` : '';
}

function getVenueName(
  venueId: string | undefined | number,
  venuesDict: MappedItems<BareVenueTemplate>
): string {
  if (!venueId) return 'Unknown';
  if (typeof venueId === 'number') return 'Unknown';
  const venue = venuesDict[venueId];
  return venue?.name ?? 'Unknown';
}

type OtherResourceInput = {
  resourceType: string;
  name: string;
  amount: number | string;
  price?: number;
};
type OtherResourceItem = { name: string; amount: number | string; price?: number };
export type OtherResourceGroup = { type: string; items: OtherResourceItem[] };

export function formatOtherResources(resources: OtherResourceInput[]): OtherResourceGroup[] {
  const grouped = groupBy(resources, 'resourceType');
  return Object.entries(grouped).map(([type, items]) => ({
    type,
    items: items.map(({ name, amount, price }) => ({ name, amount, price })),
  }));
}

export function findGroupPartyDifferences(
  oldGb: BareGroupBooking,
  newGb: BareGroupBooking,
  brand: Brand
) {
  const {
    tour_leader,
    tour_leader_cabin,
    guests,
    group_contact_name,
    sales_dept_email,
    sales_dept_phone,
    agency_contact_name,
    hospitality_desk,
    debark_date,
    embark_date,
  } = getCommonTexts(
    {
      default: brand.commonTexts,
      translations: [],
    },
    { selected: 'en' }
  );

  const changes: string[] = [];
  const oldParty = oldGb.party;
  const newParty = newGb.party;

  const simpleFields: SimpleFields[] = [
    { name: 'Name', getOld: () => oldParty.name, getNew: () => newParty.name },
    {
      name: 'Group Type',
      getOld: () => oldParty.groupType?.name,
      getNew: () => newParty.groupType?.name,
    },
    { name: group_contact_name, getOld: () => oldParty.contact, getNew: () => newParty.contact },
    { name: 'Notes', getOld: () => oldParty.notes, getNew: () => newParty.notes },
    {
      name: tour_leader,
      getOld: () => oldParty.groupGuideName,
      getNew: () => newParty.groupGuideName,
    },
    {
      name: tour_leader_cabin,
      getOld: () => oldParty.groupGuideCabin,
      getNew: () => newParty.groupGuideCabin,
    },
    { name: guests, getOld: () => oldParty.size, getNew: () => newParty.size },
    { name: 'Group Id', getOld: () => oldParty.groupId, getNew: () => newParty.groupId },
    {
      name: 'Internal Notes',
      getOld: () => oldParty.internalNotes,
      getNew: () => newParty.internalNotes,
    },
    { name: 'Booking No', getOld: () => oldParty.bookingNo, getNew: () => newParty.bookingNo },
    {
      name: sales_dept_email,
      getOld: () => oldParty.salesEmail,
      getNew: () => newParty.salesEmail,
    },
    {
      name: sales_dept_phone,
      getOld: () => oldParty.salesPhone,
      getNew: () => newParty.salesPhone,
    },
    {
      name: 'Payment Status',
      getOld: () => capitalizeClean(oldParty.paymentStatus, ['-']),
      getNew: () => capitalizeClean(newParty.paymentStatus, ['-']),
    },
    { name: 'Balance Due', getOld: () => oldParty.balanceDue, getNew: () => newParty.balanceDue },
    { name: 'Cabin Drops', getOld: () => oldParty.cabinDrops, getNew: () => newParty.cabinDrops },
    {
      name: 'Dining Experience',
      getOld: () => oldParty.diningPreference,
      getNew: () => newParty.diningPreference,
    },
    {
      name: 'Prepaid Packages',
      getOld: () => oldParty.prepaidPackages,
      getNew: () => newParty.prepaidPackages,
    },
    {
      name: agency_contact_name,
      getOld: () => oldParty.agencyContactName,
      getNew: () => newParty.agencyContactName,
    },
    { name: embark_date, getOld: () => oldParty.embark, getNew: () => newParty.embark },
    { name: debark_date, getOld: () => oldParty.debark, getNew: () => newParty.debark },
  ];

  simpleFields.forEach(field => {
    const oldVal = field.getOld();
    const newVal = field.getNew();
    if (oldVal !== newVal) {
      changes.push(
        `${capitalize(field.name)} Update: ${formatValue(oldVal)} → ${
          field.prefix ?? ''
        }${formatValue(newVal)}`
      );
    }
  });

  // Files
  const fileMessage = getFileChangeMessage(oldParty, newParty);
  if (fileMessage) changes.push(fileMessage);

  // Hospitality Desk
  if (oldParty.hospitalityDesk !== newParty.hospitalityDesk) {
    changes.push(
      `${capitalize(hospitality_desk)} Update: ${
        oldParty.hospitalityDesk ? 'Required' : 'Not Required'
      } → ${newParty.hospitalityDesk ? 'Required' : 'Not Required'}`
    );
  }

  return changes;
}
